import type { Fragment, Mark, Node as PMNode } from '@atlaskit/editor-prosemirror/model';

import type { InlineItem, TextRun } from './types';

export function filterMarks(marks: readonly Mark[], ignore: ReadonlySet<string>): readonly Mark[] {
	if (ignore.size === 0) return marks;
	if (!marks.some((m) => ignore.has(m.type.name))) return marks;
	return marks.filter((m) => !ignore.has(m.type.name));
}

// JSON.stringify is key-order-sensitive. PM normalises top-level attr keys
// via `computeAttrs`, but nested object values are preserved verbatim. Sort
// recursively for an order-independent stable key.
export const stableStringify = (v: unknown): string => {
	if (v === null || typeof v !== 'object') return JSON.stringify(v) ?? 'null';
	if (Array.isArray(v)) return `[${v.map(stableStringify).join(',')}]`;
	const o = v as Record<string, unknown>;
	return `{${Object.keys(o)
		.sort()
		.map((k) => `${JSON.stringify(k)}:${stableStringify(o[k])}`)
		.join(',')}}`;
};

/**
 * Like `a.eq(b)` but treats marks of ignored types as invisible. Used by the
 * block-level prefix/suffix strip and the dispatcher short-circuit so a doc
 * whose only changes are in ignored marks bails out cheaply.
 *
 * When `ignore` is empty, delegates straight to PM's `Node.eq`.
 *
 * Inline content needs special handling: PM auto-merges adjacent text nodes
 * with identical marks, so two paragraphs that are character-stream-equivalent
 * after filtering ignored marks can have different `childCount`. We compare
 * via a per-character token stream in that case.
 */
export function nodeEqIgnoring(a: PMNode, b: PMNode, ignore: ReadonlySet<string>): boolean {
	if (ignore.size === 0) return a.eq(b);
	if (a.eq(b)) return true; // fast path; covers the common case
	if (a.type !== b.type) return false;
	if (!sameMarkSet(filterMarks(a.marks, ignore), filterMarks(b.marks, ignore))) return false;
	// PM attrs are plain JSON values; compare them independent of object key order.
	if (stableStringify(a.attrs ?? {}) !== stableStringify(b.attrs ?? {})) return false;
	if (a.isText) {
		return a.text === b.text;
	}
	if (a.type.inlineContent) {
		return inlineContentEqIgnoring(a.content, b.content, ignore);
	}
	if (a.childCount !== b.childCount) return false;
	for (let i = 0; i < a.childCount; i++) {
		if (!nodeEqIgnoring(a.child(i), b.child(i), ignore)) return false;
	}
	return true;
}

/**
 * Inline-aware equality: walks two inline-content fragments as a character
 * stream + non-text token stream, applying mark filtering per character. This
 * sidesteps PM's text-node auto-merging differences between the two sides.
 */
function inlineContentEqIgnoring(a: Fragment, b: Fragment, ignore: ReadonlySet<string>): boolean {
	type Tok = { ch: string; kind: 'char'; marks: readonly Mark[] } | { kind: 'node'; node: PMNode };

	const tokens = (frag: Fragment): Tok[] => {
		const out: Tok[] = [];
		for (let i = 0; i < frag.childCount; i++) {
			const child = frag.child(i);
			if (child.isText && child.text !== undefined) {
				const marks = filterMarks(child.marks, ignore);
				for (const ch of child.text) out.push({ kind: 'char', ch, marks });
			} else {
				out.push({ kind: 'node', node: child });
			}
		}
		return out;
	};

	const at = tokens(a);
	const bt = tokens(b);
	if (at.length !== bt.length) return false;
	for (let i = 0; i < at.length; i++) {
		const x = at[i];
		const y = bt[i];
		if (x.kind !== y.kind) return false;
		if (x.kind === 'char' && y.kind === 'char') {
			if (x.ch !== y.ch) return false;
			if (!sameMarkSet(x.marks, y.marks)) return false;
		} else if (x.kind === 'node' && y.kind === 'node') {
			if (!nodeEqIgnoring(x.node, y.node, ignore)) return false;
		}
	}
	return true;
}

function sameMarkSet(a: readonly Mark[], b: readonly Mark[]): boolean {
	if (a.length !== b.length) return false;
	return a.every((m) => b.some((n) => n.eq(m)));
}

/**
 * Like `a.content.eq(b.content)` but treats ignored marks as invisible. Lets
 * ReplaceAround detection match "same content, different wrapper" even when
 * the old content carries an inline comment that the new content lacks.
 *
 * `a` and `b` are the PARENT nodes; we use them to decide whether their
 * contents are inline (needs character-stream comparison to handle PM's
 * auto-merging) or block (plain child-by-child recursion).
 */
export function contentEqIgnoring(a: PMNode, b: PMNode, ignore: ReadonlySet<string>): boolean {
	if (a.type.inlineContent || b.type.inlineContent) {
		return inlineContentEqIgnoring(a.content, b.content, ignore);
	}
	if (a.childCount !== b.childCount) return false;
	for (let i = 0; i < a.childCount; i++) {
		if (!nodeEqIgnoring(a.child(i), b.child(i), ignore)) return false;
	}
	return true;
}

export const isTextRun = (i: InlineItem): i is TextRun => i.kind === 'textRun';
